/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { templatizeEndpoint } from '../schema/templatizeEndpoint';

/** The subset of a thrown value this reads. Everything else is ignored. */
type LoggableError = {
  message?: unknown;
  code?: unknown;
  config?: { method?: unknown; url?: unknown };
  response?: { status?: unknown; data?: { code?: unknown } };
};

const asText = (value: unknown): string | undefined =>
  typeof value === 'string' && value ? value : undefined;

/**
 * Reduces a thrown value to a summary that carries no personal data.
 *
 * Never pass a raw error to a logger. An Axios rejection holds the request body
 * on `config.data` — a failed change-password carries the new password there —
 * and the bearer token on `config.headers`; `response.data` can echo back
 * whatever was submitted. Only the status, the backend error code, a
 * templatized endpoint and the error message survive.
 */
export function describeError(error: unknown): string {
  if (typeof error === 'string') {
    return error;
  }
  if (!error || typeof error !== 'object') {
    return 'unknown error';
  }

  const { message, code, config, response } = error as LoggableError;
  const parts: string[] = [];

  if (typeof response?.status === 'number') {
    parts.push(`status=${response.status}`);
  }

  const errorCode = asText(response?.data?.code) ?? asText(code);
  if (errorCode) {
    parts.push(`code=${errorCode}`);
  }

  const url = asText(config?.url);
  if (url) {
    parts.push(
      templatizeEndpoint({ method: asText(config?.method) ?? 'get', url }),
    );
  }

  const text = asText(message);
  if (text) {
    parts.push(text);
  }

  return parts.length > 0 ? parts.join(' ') : 'unknown error';
}
